import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  type Type,
} from '@angular/core';

import { ThumbUpDownFeedbackComponent } from '@core/intelligence-ui';
import { AskDecisionOption, ChatMessage, ChatPart } from '@core/tellent-assistant-types';
import { splitAssistantMessage } from '@core/tellent-assistant-util';
import { ClipboardCopy, IconButtonComponent } from '@core/user-interface-legacy';

import { TellentAssistantAskDecisionComponent } from '../parts/ask-decision/ask-decision.component';
import {
  TellentAssistantProposalPreviewComponent,
  type ProposalResultLink,
} from '../parts/proposal-preview/proposal-preview.component';
import { TellentAssistantTextPartComponent } from '../parts/text-part/text-part.component';
import {
  TellentAssistantThinkingSectionComponent,
  type ThinkingRunState,
} from '../parts/thinking-section/thinking-section.component';
import { TellentAssistantToolCallPartComponent } from '../parts/tool-call-part/tool-call-part.component';
import { TellentAssistantToolErrorPillComponent } from '../parts/tool-error-pill/tool-error-pill.component';

export type RenderToolStatus = 'running' | 'done' | 'error';

export type RenderComponentResolver = (
  toolName: string,
  args: unknown,
  status: RenderToolStatus,
) => Type<unknown> | undefined;

/** A decision made on a proposal card, re-emitted for the host to dispatch. */
export interface ProposalDecisionEvent {
  part: Extract<ChatPart, { kind: 'propose-action' }>;
  decision: 'confirm' | 'skip';
}

@Component({
  selector: 'tellent-assistant-message',
  standalone: true,
  imports: [
    IconButtonComponent,
    TellentAssistantAskDecisionComponent,
    TellentAssistantProposalPreviewComponent,
    TellentAssistantTextPartComponent,
    TellentAssistantThinkingSectionComponent,
    TellentAssistantToolCallPartComponent,
    TellentAssistantToolErrorPillComponent,
    ThumbUpDownFeedbackComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.role-assistant]': 'isAssistant()',
    '[class.role-user]': '!isAssistant()',
  },
  template: `
    <article [class.role-assistant]="isAssistant()" [class.role-user]="!isAssistant()">
      <div class="body">
        @if (isAssistant()) {
          @if (timeline().length > 0 || runState() === 'running') {
            <tellent-assistant-thinking-section
              [timeline]="timeline()"
              [runState]="runState()"
              [toolTitles]="toolTitles()"
            />
          }
          @for (entry of body(); track entry.key) {
            @switch (entry.kind) {
              @case ('text') {
                <tellent-assistant-text-part [content]="entry.content" [done]="entry.done" />
              }
              @case ('render-tool') {
                @if (
                  resolveRenderComponent()(entry.part.name, entry.part.args, entry.part.status);
                  as renderComponent
                ) {
                  <tellent-assistant-tool-call-part
                    [name]="entry.part.name"
                    [title]="toolTitles()[entry.part.name]"
                    [toolCallId]="entry.part.toolCallId"
                    [args]="entry.part.args"
                    [status]="entry.part.status"
                    [durationMs]="entry.part.durationMs ?? null"
                    [renderComponent]="renderComponent"
                  />
                }
              }
              @case ('render-error') {
                <tellent-assistant-tool-error-pill [toolName]="entry.toolName" />
              }
              @case ('propose-action') {
                <tellent-assistant-proposal-preview
                  [args]="entry.part.args"
                  [display]="entry.part.display"
                  [confirm]="entry.part.confirm"
                  [resolvedStatus]="actionResolver()(entry.part.proposalId)"
                  [resultLink]="resultLinkResolver()(entry.part.proposalId)"
                  [busy]="decisionBusyResolver()(entry.part.proposalId)"
                  [errored]="decisionErrorResolver()(entry.part.proposalId)"
                  (confirmed)="proposalDecided.emit({ part: entry.part, decision: 'confirm' })"
                  (skipped)="proposalDecided.emit({ part: entry.part, decision: 'skip' })"
                />
              }
              @case ('action-executed') {
                <tellent-assistant-proposal-preview
                  [args]="entry.part.args"
                  [confirm]="entry.part.confirm"
                  [auto]="true"
                />
              }
              @case ('ask-decision') {
                @if (!decisionsCollapsed()) {
                  <tellent-assistant-ask-decision
                    [question]="entry.part.question"
                    [options]="entry.part.options"
                    [confirmLabel]="entry.part.confirmLabel"
                    [disabled]="decisionsDisabled()"
                    (answered)="decisionAnswered.emit({ option: $event })"
                    (skipped)="decisionSkipped.emit()"
                  />
                }
              }
            }
          }
          @if (!pending() && runState() === 'done') {
            <div class="actions">
              @if (showFeedback()) {
                <thumb-up-down-feedback [showLabel]="false" [reverseThumbsUp]="true" />
              }
              <rt-icon-button
                iconName="content-copy"
                size="small"
                variant="plain"
                class="copy"
                (click)="copy()"
              />
            </div>
          }
        } @else {
          @for (part of message().parts; track partKey(part)) {
            @switch (part.kind) {
              @case ('text') {
                <tellent-assistant-text-part
                  [content]="message().displayText ?? part.content"
                  [done]="part.done"
                />
              }
            }
          }
        }
      </div>
    </article>
  `,
  styleUrl: './tellent-assistant-message.component.less',
})
export class TellentAssistantMessageComponent {
  private readonly clipboard = inject(ClipboardCopy);

  protected readonly isAssistant = computed(() => this.message().role === 'assistant');

  protected readonly view = computed(() =>
    splitAssistantMessage(this.message(), this.renderToolNames()),
  );

  protected readonly body = computed(() => this.view().body);
  protected readonly timeline = computed(() => this.view().timeline);

  protected readonly runState = computed<ThinkingRunState>(() => {
    if (this.pending()) return 'running';
    const message = this.message();
    if (message.role !== 'assistant') return 'done';
    if (message.parts.length === 0) return 'running';
    const lastTool = message.parts.findLast(
      (part): part is Extract<ChatPart, { kind: 'tool-call' }> => part.kind === 'tool-call',
    );
    if (lastTool?.status === 'running') return 'running';
    const lastText = message.parts.findLast(
      (part): part is Extract<ChatPart, { kind: 'text' }> => part.kind === 'text',
    );
    if (lastText && !lastText.done) return 'running';
    const resultIds = new Set(
      message.parts
        .filter(
          (part): part is Extract<ChatPart, { kind: 'tool-result' }> => part.kind === 'tool-result',
        )
        .map(part => part.toolCallId),
    );
    // Sandbox-origin calls have no paired tool-result — skip them in the unmatched check.
    const hasUnmatchedCall = message.parts.some(
      part =>
        part.kind === 'tool-call' && part.origin !== 'sandbox' && !resultIds.has(part.toolCallId),
    );
    return hasUnmatchedCall ? 'running' : 'done';
  });

  public readonly message = input.required<ChatMessage>();
  public readonly resolveRenderComponent = input<RenderComponentResolver>(() => undefined);
  public readonly renderToolNames = input<ReadonlySet<string>>(new Set<string>());
  public readonly toolTitles = input<Record<string, string>>({});
  public readonly pending = input<boolean>(false);
  public readonly showFeedback = input<boolean>(true);
  public readonly actionResolver = input<(proposalId: string) => 'confirmed' | 'cancelled' | null>(
    () => null,
  );
  public readonly resultLinkResolver = input<(proposalId: string) => ProposalResultLink | null>(
    () => null,
  );
  public readonly decisionBusyResolver = input<(proposalId: string) => boolean>(() => false);
  public readonly decisionErrorResolver = input<(proposalId: string) => boolean>(() => false);
  /** Hides ask-decision cards once a later user turn made them moot. */
  public readonly decisionsCollapsed = input<boolean>(false);
  public readonly decisionsDisabled = input<boolean>(false);

  public readonly proposalDecided = output<ProposalDecisionEvent>();
  public readonly decisionAnswered = output<{ option: AskDecisionOption }>();
  public readonly decisionSkipped = output<void>();

  protected partKey(part: ChatPart): string {
    switch (part.kind) {
      case 'text':
        return `text-${part.messageId}`;
      case 'reasoning':
        return `reasoning-${part.messageId}`;
      case 'tool-call':
      case 'tool-result':
        return `${part.kind}-${part.toolCallId}`;
      case 'render-component':
        return `render-component-${part.componentId}`;
      case 'propose-action':
        return `propose-action-${part.proposalId}`;
      case 'action-executed':
        return `action-executed-${part.executionId}`;
      case 'ask-decision':
        return `ask-decision-${part.decisionId}`;
      default:
        return '';
    }
  }

  protected copy(): void {
    const text = this.message()
      .parts.filter(p => p.kind === 'text')
      .map(p => (<Extract<ChatPart, { kind: 'text' }>>p).content)
      .join('\n');
    this.clipboard.fromString(text);
  }
}
